import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';

const STORAGE_KEYS = {
  token: 'lion_delivery_admin_token',
  language: 'lion_delivery_admin_language',
};

const API_BASE_URL = 'https://atagamingshop.com/api/lion-deli';

const translations = {
  en: {
    brand: 'Lion Delivery',
    slogan: 'Fast & Reliable Delivery',
    loginTitle: 'Admin Login',
    loginSubtitle:
      'Sign in with your admin account to access the user creation panel.',
    loginCaption: 'Secure access for Lion Delivery administration.',
    email: 'Email Address',
    password: 'Password',
    login: 'Login',
    loggingIn: 'Signing in...',
    createUser: 'Create User',
    createSubtitle:
      'Create a new account for staff or system users from this admin panel.',
    fullName: 'Full Name',
    phoneNumber: 'Phone Number',
    location: 'Location',
    shopName: 'Shop Name',
    userRole: 'User Role',
    profileImage: 'Profile Image',
    optional: 'Optional',
    admin: 'Admin',
    user: 'User',
    createAccount: 'Create Account',
    creating: 'Creating...',
    clear: 'Clear',
    logout: 'Logout',
    currentAdmin: 'Current Admin',
    language: 'Language',
    english: 'English',
    myanmar: 'Myanmar',
    successLogin: 'Login successful.',
    successCreate: 'User created successfully.',
    loginFailed: 'Login failed.',
    createFailed: 'Unable to create user.',
    fieldRequired: 'Please fill in all required fields.',
    tokenMissing: 'You need to log in first.',
    uploadHint: 'JPG, PNG or WEBP image',
    welcome: 'Welcome back',
    helperTitle: 'Admin access only',
    helperBody:
      'Manage the delivery team with the same trusted Lion Delivery color system and a cleaner admin workflow.',
  },
  my: {
    brand: 'Lion Delivery',
    slogan: 'မြန်ဆန်ပြီး ယုံကြည်စိတ်ချရသော ပို့ဆောင်ရေး',
    loginTitle: 'Admin Login',
    loginSubtitle:
      'User အသစ်ဖန်တီးနိုင်ဖို့ admin အကောင့်နဲ့ အရင်ဝင်ပေးပါ။',
    loginCaption: 'Lion Delivery administration အတွက် လုံခြုံသော ဝင်ရောက်ခွင့်',
    email: 'အီးမေးလ်',
    password: 'စကားဝှက်',
    login: 'ဝင်မည်',
    loggingIn: 'ဝင်နေပါသည်...',
    createUser: 'User အသစ်ဖန်တီးမည်',
    createSubtitle:
      'Admin panel ကနေ staff သို့မဟုတ် system user account အသစ်ဖန်တီးနိုင်ပါသည်။',
    fullName: 'အမည်',
    phoneNumber: 'ဖုန်းနံပါတ်',
    location: 'တည်နေရာ',
    shopName: 'ဆိုင်အမည်',
    userRole: 'User Role',
    profileImage: 'Profile ပုံ',
    optional: 'မထည့်လည်းရ',
    admin: 'Admin',
    user: 'User',
    createAccount: 'Account ဖန်တီးမည်',
    creating: 'ဖန်တီးနေပါသည်...',
    clear: 'ရှင်းမည်',
    logout: 'ထွက်မည်',
    currentAdmin: 'လက်ရှိ Admin',
    language: 'ဘာသာစကား',
    english: 'English',
    myanmar: 'မြန်မာ',
    successLogin: 'Login အောင်မြင်ပါသည်။',
    successCreate: 'User ဖန်တီးခြင်း အောင်မြင်ပါသည်။',
    loginFailed: 'Login မအောင်မြင်ပါ။',
    createFailed: 'User ဖန်တီး၍ မရပါ။',
    fieldRequired: 'လိုအပ်သော field များအားလုံး ဖြည့်ပေးပါ။',
    tokenMissing: 'အရင် login ဝင်ပေးရပါမည်။',
    uploadHint: 'JPG, PNG သို့မဟုတ် WEBP ပုံ',
    welcome: 'ပြန်လည်ကြိုဆိုပါတယ်',
    helperTitle: 'Admin များသာ အသုံးပြုရန်',
    helperBody:
      'Lion Delivery ရဲ့ ယုံကြည်ရတဲ့ color theme နဲ့ ပိုသန့်တဲ့ admin workflow ကို ဒီနေရာမှာ အသုံးပြုနိုင်ပါတယ်။',
  },
};

const initialCreateForm = {
  email: '',
  phone_number: '',
  location: '',
  name: '',
  shop_name: '',
  password: '',
  user_role: 'admin',
};

function App() {
  const [language, setLanguage] = useState(
    localStorage.getItem(STORAGE_KEYS.language) || 'en'
  );
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEYS.token) || '');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [createImage, setCreateImage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [notice, setNotice] = useState({ type: 'idle', message: '' });

  const t = useMemo(() => translations[language] || translations.en, [language]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.language, language);
  }, [language]);

  const sendRequest = useCallback(
    async ({
      path,
      method = 'GET',
      body,
      useAuth = false,
      isMultipart = false,
      accessToken = token,
    }) => {
      const headers = {};

      if (body && !isMultipart) {
        headers['Content-Type'] = 'application/json';
      }

      if (useAuth) {
        if (!accessToken.trim()) {
          setNotice({ type: 'error', message: t.tokenMissing });
          return null;
        }
        headers.Authorization = `Bearer ${accessToken.trim()}`;
      }

      try {
        const response = await fetch(`${API_BASE_URL}${path}`, {
          method,
          headers,
          body:
            body === undefined || body === null
              ? undefined
              : isMultipart
                ? body
                : JSON.stringify(body),
        });

        const rawText = await response.text();
        let data;

        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch (error) {
          data = { raw: rawText };
        }

        return { ok: response.ok, data };
      } catch (error) {
        return { ok: false, data: { message: error.message } };
      }
    },
    [t.tokenMissing, token]
  );

  const buildFormData = (payload, imageFile) => {
    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    if (imageFile) {
      formData.append('profile_image', imageFile);
    }

    return formData;
  };

  const fetchCurrentUser = useCallback(
    async (accessToken = token) => {
      const result = await sendRequest({
        path: '/auth/get',
        method: 'GET',
        useAuth: true,
        accessToken,
      });

      if (result?.ok && result.data?.data) {
        setCurrentUser(result.data.data);
      }
    },
    [sendRequest, token]
  );

  useEffect(() => {
    if (token) {
      fetchCurrentUser(token);
    }
  }, [fetchCurrentUser, token]);

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!loginForm.email.trim() || !loginForm.password.trim()) {
      setNotice({ type: 'error', message: t.fieldRequired });
      return;
    }

    setAuthLoading(true);
    const result = await sendRequest({
      path: '/auth/login',
      method: 'POST',
      body: {
        email: loginForm.email.trim(),
        password: loginForm.password,
      },
    });
    setAuthLoading(false);

    const accessToken = result?.data?.data?.access_token;
    if (result?.ok && accessToken) {
      localStorage.setItem(STORAGE_KEYS.token, accessToken);
      setToken(accessToken);
      setNotice({ type: 'success', message: t.successLogin });
      await fetchCurrentUser(accessToken);
      return;
    }

    setNotice({
      type: 'error',
      message: result?.data?.message || t.loginFailed,
    });
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();

    const required = [
      createForm.email,
      createForm.phone_number,
      createForm.location,
      createForm.name,
      createForm.shop_name,
      createForm.password,
      createForm.user_role,
    ];

    if (required.some((value) => !value.trim())) {
      setNotice({ type: 'error', message: t.fieldRequired });
      return;
    }

    setCreateLoading(true);
    const result = await sendRequest({
      path: '/auth/create',
      method: 'POST',
      body: buildFormData(
        {
          email: createForm.email.trim(),
          phone_number: createForm.phone_number.trim(),
          location: createForm.location.trim(),
          name: createForm.name.trim(),
          shop_name: createForm.shop_name.trim(),
          password: createForm.password,
          user_role: createForm.user_role,
        },
        createImage
      ),
      useAuth: true,
      isMultipart: true,
    });
    setCreateLoading(false);

    if (result?.ok) {
      setCreateForm(initialCreateForm);
      setCreateImage(null);
      setNotice({ type: 'success', message: t.successCreate });
      return;
    }

    setNotice({
      type: 'error',
      message: result?.data?.message || t.createFailed,
    });
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.token);
    setToken('');
    setCurrentUser(null);
    setLoginForm({ email: '', password: '' });
    setNotice({ type: 'idle', message: '' });
  };

  const clearCreateForm = () => {
    setCreateForm(initialCreateForm);
    setCreateImage(null);
    setNotice({ type: 'idle', message: '' });
  };

  const renderLanguageSwitch = (variant = 'dark') => (
    <div className={`language-switch language-switch--${variant}`} aria-label={t.language}>
      <button
        type="button"
        className={language === 'en' ? 'is-active' : ''}
        onClick={() => setLanguage('en')}
      >
        {t.english}
      </button>
      <button
        type="button"
        className={language === 'my' ? 'is-active' : ''}
        onClick={() => setLanguage('my')}
      >
        {t.myanmar}
      </button>
    </div>
  );

  const renderLoginView = () => (
    <main className="auth-layout">
      <section className="auth-hero">
        <div className="auth-hero__top">
          <div className="auth-hero__badge">LD</div>
          {renderLanguageSwitch('hero')}
        </div>

        <div className="auth-hero__content">
          <p className="auth-hero__eyebrow">{t.loginCaption}</p>
          <h1>{t.welcome}</h1>
          <p className="auth-hero__copy">{t.helperBody}</p>
        </div>

        <div className="auth-hero__footer">
          <strong>{t.helperTitle}</strong>
          <span>{t.slogan}</span>
        </div>
      </section>

      <section className="auth-card">
        <div className="auth-card__head">
          <p className="section-kicker">{t.brand}</p>
          <h2>{t.loginTitle}</h2>
          <p>{t.loginSubtitle}</p>
        </div>

        <form className="form-grid" onSubmit={handleLogin}>
          <label className="field">
            <span>{t.email}</span>
            <input
              type="email"
              value={loginForm.email}
              onChange={(event) =>
                setLoginForm((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder="admin@example.com"
            />
          </label>

          <label className="field">
            <span>{t.password}</span>
            <input
              type="password"
              value={loginForm.password}
              onChange={(event) =>
                setLoginForm((prev) => ({ ...prev, password: event.target.value }))
              }
              placeholder="secret123"
            />
          </label>

          {notice.message ? (
            <div className={`notice notice--${notice.type}`}>{notice.message}</div>
          ) : null}

          <button
            className="primary-button primary-button--full"
            type="submit"
            disabled={authLoading}
          >
            {authLoading ? t.loggingIn : t.login}
          </button>
        </form>
      </section>
    </main>
  );

  const renderDashboardView = () => (
    <div className="dashboard-shell">
      <header className="dashboard-topbar">
        <div className="dashboard-topbar__brand">
          <div className="brand-mark">LD</div>
          <div>
            <div className="brand-name">{t.brand}</div>
            <div className="brand-slogan">{t.slogan}</div>
          </div>
        </div>

        <div className="dashboard-topbar__actions">
          <div className="control-chip">
            <span className="control-chip__label">{t.language}</span>
            {renderLanguageSwitch('panel')}
          </div>
          <button type="button" className="ghost-button" onClick={handleLogout}>
            {t.logout}
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        <section className="dashboard-hero">
          <div className="dashboard-hero__avatar">
            {(currentUser?.name || currentUser?.email || 'A').slice(0, 1).toUpperCase()}
          </div>
          <div>
            <p className="dashboard-hero__eyebrow">{t.currentAdmin}</p>
            <h1>{currentUser?.name || t.brand}</h1>
            <p className="dashboard-hero__meta">
              {currentUser?.email || '-'}
              {currentUser?.user_role ? ` • ${currentUser.user_role}` : ''}
            </p>
          </div>
        </section>

        <section className="create-panel">
          <div className="create-panel__head">
            <div>
              <p className="section-kicker">{t.brand}</p>
              <h2>{t.createUser}</h2>
              <p>{t.createSubtitle}</p>
            </div>
          </div>

          <form className="form-grid" onSubmit={handleCreateUser}>
            <div className="form-grid form-grid--two">
              <label className="field">
                <span>{t.fullName}</span>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
              </label>

              <label className="field">
                <span>{t.email}</span>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                />
              </label>

              <label className="field">
                <span>{t.phoneNumber}</span>
                <input
                  type="text"
                  value={createForm.phone_number}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      phone_number: event.target.value,
                    }))
                  }
                />
              </label>

              <label className="field">
                <span>{t.location}</span>
                <input
                  type="text"
                  value={createForm.location}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, location: event.target.value }))
                  }
                />
              </label>

              <label className="field">
                <span>{t.shopName}</span>
                <input
                  type="text"
                  value={createForm.shop_name}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, shop_name: event.target.value }))
                  }
                />
              </label>

              <label className="field">
                <span>{t.password}</span>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, password: event.target.value }))
                  }
                />
              </label>
            </div>

            <div className="form-grid form-grid--two">
              <label className="field">
                <span>{t.userRole}</span>
                <select
                  value={createForm.user_role}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, user_role: event.target.value }))
                  }
                >
                  <option value="admin">{t.admin}</option>
                  <option value="user">{t.user}</option>
                </select>
              </label>

              <label className="field">
                <span>
                  {t.profileImage} ({t.optional})
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setCreateImage(event.target.files?.[0] || null)}
                />
                <small className="field-hint">{t.uploadHint}</small>
              </label>
            </div>

            {notice.message ? (
              <div className={`notice notice--${notice.type}`}>{notice.message}</div>
            ) : null}

            <div className="button-row">
              <button className="primary-button" type="submit" disabled={createLoading}>
                {createLoading ? t.creating : t.createAccount}
              </button>
              <button className="ghost-button" type="button" onClick={clearCreateForm}>
                {t.clear}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );

  return <div className="app-shell">{token ? renderDashboardView() : renderLoginView()}</div>;
}

export default App;
